import { isResource, resolveId } from '@medplum/core'
import type {
  AllergyIntolerance,
  CodeableConcept,
  Encounter,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class ExtendedAllergy extends ExtendedResource<AllergyIntolerance> {
  static readonly RESOURCE_TYPE = 'AllergyIntolerance'

  static isInstance(value: unknown): value is AllergyIntolerance {
    return isResource(value) && isOfType(value, 'AllergyIntolerance')
  }

  static searchQuery({
    participant,
  }: {
    participant?: Reference<Patient>
  }): QueryTypes {
    const query: QueryTypes = {}
    if (participant) {
      query.patient = participant
    }
    query['clinical-status'] = 'active'
    return query
  }

  get name(): string {
    return (
      this.resource.code?.text ??
      displayFromCodeableConcept(this.resource.code) ??
      'Unknown Allergy'
    )
  }

  get reaction(): string {
    return (
      this.resource.reaction?.[0]?.manifestation?.[0]?.text ??
      'No reaction recorded'
    )
  }

  set reaction(reaction: string) {
    this.resource.reaction ??= []
    this.resource.reaction[0] ??= { manifestation: [] }
    this.resource.reaction[0].manifestation[0] ??= {}
    this.resource.reaction[0].manifestation[0].text = reaction
  }

  get isActive(): boolean {
    return codeFromCodeableConcept(this.resource.clinicalStatus) === 'active'
  }

  get severity(): 'mild' | 'moderate' | 'severe' | undefined {
    return this.resource.reaction?.[0]?.severity
  }

  set severity(severity: 'mild' | 'moderate' | 'severe' | undefined) {
    this.resource.reaction ??= []
    this.resource.reaction[0] ??= { manifestation: [] }
    this.resource.reaction[0].severity = severity
  }

  get clinicalStatus(): CodeableConcept | undefined {
    return this.resource.clinicalStatus
  }

  set clinicalStatus(status: CodeableConcept | undefined) {
    this.resource.clinicalStatus = status
  }

  get manifestation(): string {
    return this.resource.reaction?.[0]?.manifestation?.[0]?.text ?? ''
  }

  get category(): string | undefined {
    return this.resource.category?.[0]
  }

  get notes(): string[] | undefined {
    return this.resource.note?.map((n) => n.text)
  }

  get recordedDate(): string | undefined {
    return this.resource.recordedDate
  }

  get onsetDateTime(): string | undefined {
    return this.resource.onsetDateTime
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource.encounter
  }

  set encounter(value: Reference<Encounter> | undefined) {
    this.resource.encounter = value
  }

  get patient(): Reference<Patient> | undefined {
    return this.resource.patient
  }

  get verificationStatus(): string | undefined {
    return codeFromCodeableConcept(this.resource.verificationStatus)
  }

  get lastOccurrence(): string | undefined {
    return this.resource.lastOccurrence
  }

  get type(): AllergyIntolerance['type'] {
    return this.resource.type
  }

  set type(type: AllergyIntolerance['type']) {
    this.resource.type = type
  }

  get criticality(): AllergyIntolerance['criticality'] {
    return this.resource.criticality
  }

  get searchKeywords(): string[] {
    return [this.name]
  }

  protected getExportFields() {
    return {
      id: this.id,
      name: this.name,
      reaction: this.reaction,
      isActive: this.isActive,
      severity: this.severity,
      manifestation: this.manifestation,
      clinicalStatus: codeFromCodeableConcept(this.clinicalStatus),
      recordedDate: this.recordedDate,
      patientId: resolveId(this.patient),
      category: this.category,
      type: this.type,
      criticality: this.criticality,
      verificationStatus: this.verificationStatus,
      lastOccurrence: this.lastOccurrence,
      notes: this.notes,
    }
  }
}
